import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { numberWithCommas, buildStringValue } from 'site-modules/shared/utils/string';
import { compact, get } from 'lodash-es';
import { isPickupType } from 'site-modules/shared/utils/is-pickup-type';
import { maybe } from 'site-modules/shared/utils/incentives/gql-incentives-filter';

export function getVehicleSummaryData(vinData) {
  const { type, vehicleInfo = {}, vin, stockNumber } = vinData;
  const { partsInfo = {}, styleInfo, vehicleColors, mileage } = vehicleInfo;
  const {
    engineType,
    driveTrain,
    transmission,
    enginePower,
    electricityRange,
    batteryChargeTime,
    bedLengthMeasurement,
    rearWheels,
    cylinders,
    engineSize,
    transmissionInfo,
  } = partsInfo;
  const isUsedType = isUsed(type);
  const loweredTransmission = transmission && transmission.toLowerCase();
  const transformedMileage = isUsedType && mileage ? numberWithCommas(mileage) : null;
  const exteriorColor = get(vehicleColors, 'exterior', {});
  const interiorColor = get(vehicleColors, 'interior', {});
  const exteriorColorName = get(exteriorColor, 'name', '');
  const interiorColorName = get(interiorColor, 'name', '');
  const epaCityMPG = get(styleInfo, 'fuel.epaCityMPG', '');
  const epaHighwayMPG = get(styleInfo, 'fuel.epaHighwayMPG', '');
  const epaCombinedMPG = get(styleInfo, 'fuel.epaCombinedMPG', '');
  const epaElectricCityMPG = get(styleInfo, 'fuel.epaElectricCityMPG', '');
  const epaElectricHighwayMPG = get(styleInfo, 'fuel.epaElectricHighwayMPG', '');
  const epaElectricCombinedMPG = get(styleInfo, 'fuel.epaElectricCombinedMPG', '');
  const isPickup = isPickupType(get(styleInfo, 'bodyType', ''));
  const numberOfSeats = get(styleInfo, 'numberOfSeats', '');
  const towingCapacity = get(styleInfo, 'towingCapacity');
  const payloadCapacity = get(styleInfo, 'payloadCapacity');
  const speed = get(transmissionInfo, 'speed');
  const engineSizeString = engineSize ? `${engineSize}L` : null;
  const cylindersString = cylinders ? `${cylinders}cyl` : null;
  const speedString = speed ? `${speed}-Speed` : null;
  const countryMap = {
    'united states': 'USA',
    turkey: 'türkiye',
  };

  const assemblyCountry = maybe(styleInfo?.assemblyCountry)
    .map(country => country.toLowerCase())
    .map(country => countryMap[country] ?? country)
    .unpack();

  const styleValue = get(styleInfo, 'style', '')
    .replace(/\s*\([^)]+\)|\s*w\/Prod\. End(?:\s+[\d/]+)?/gi, '')
    .trim();

  return [
    {
      name: 'style',
      value: styleValue,
    },
    {
      name: 'mileage',
      value: transformedMileage,
    },
    {
      name: 'exteriorColor',
      value: exteriorColorName,
    },
    {
      name: 'interiorColor',
      value: interiorColorName,
    },
    {
      name: 'engineType',
      value: compact([buildStringValue(engineSizeString, cylindersString), engineType]).join(', '),
    },
    {
      name: 'transmission',
      value: buildStringValue(speedString, loweredTransmission),
    },
    {
      name: 'driveTrain',
      value: driveTrain,
    },
    {
      name: 'mpg',
      value: epaCityMPG || epaHighwayMPG || epaCombinedMPG, // only to define whether mpg string will have any value
    },
    {
      name: 'electricityRange',
      value: electricityRange,
    },
    {
      name: 'batteryChargeTime',
      value: batteryChargeTime,
    },
    {
      name: 'mpge',
      value: epaElectricCityMPG || epaElectricHighwayMPG || epaElectricCombinedMPG, // only to define whether mpg string will have any value
    },
    {
      name: 'enginePower',
      value: enginePower,
    },
    {
      name: 'towingCapacity',
      value: towingCapacity ? numberWithCommas(towingCapacity) : '',
    },
    {
      name: 'payloadCapacity',
      value: payloadCapacity ? numberWithCommas(payloadCapacity) : '',
    },
    {
      name: 'bedLengthMeasurement',
      value: isPickup ? bedLengthMeasurement : '', // show only for Pickup
    },
    {
      name: 'rearWheels',
      value: isPickup ? rearWheels : '', // show only for Pickup
    },
    {
      name: 'numberOfSeats',
      value: numberOfSeats,
    },
    {
      name: 'vin',
      value: vin,
    },
    {
      name: 'stock',
      value: stockNumber,
    },
    { name: 'finalAssembly', value: assemblyCountry },
  ].filter(({ value }) => !!value); // remove items with empty value
}
